import { useState, useEffect } from 'react';
import { apiFetch } from '../App';

function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: color || 'var(--accent)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MarketBar({ market, percentage, weightedCopies, max }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>{market}</span>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{percentage}% · {weightedCopies.toLocaleString()} copies</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${(weightedCopies / max) * 100}%`,
          background: 'var(--accent)',
          borderRadius: 3,
          transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

export default function BotswanaPulse() {
  const [pulse, setPulse] = useState(null);
  const [mostCopied, setMostCopied] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Promise.all([
      apiFetch('/pulse/latest'),
      apiFetch('/pulse/most-copied?limit=10'),
    ]).then(([pulseData, copiedData]) => {
      setPulse(pulseData.available ? pulseData.pulse : null);
      setMostCopied(copiedData.slips || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isMobile = width < 768;

  function formatTime(date) {
    return new Date(date).toLocaleTimeString('en-BW', { hour: '2-digit', minute: '2-digit' });
  }

  function formatNumber(n) {
    return n?.toLocaleString() || '0';
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(236,72,153,0.08), rgba(139,92,246,0.04))',
        border: '1px solid rgba(236,72,153,0.2)',
        borderRadius: 'var(--radius)', padding: '16px 20px',
        display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <div style={{ fontSize: 28, flexShrink: 0 }}>🇧🇼</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>Botswana Pulse</div>
            <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(236,72,153,0.15)', color: '#ec4899', fontWeight: 700, letterSpacing: 1 }}>PREMIUM</span>
            <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(0,229,176,0.1)', color: 'var(--accent)', fontWeight: 700 }}>
              {pulse ? `Updated ${formatTime(pulse.generatedAt)}` : 'Awaiting data'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
            Real-time intelligence on what Botswana is betting right now. Aggregated from public Betway slips — updated 4 times daily.
          </div>
        </div>
      </div>

      {/* No data state */}
      {!pulse && (
        <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📡</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
            Pulse data not yet available
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.7, maxWidth: 400, margin: '0 auto' }}>
            Betway resets public slips daily. New slips accumulate throughout the day. The Pulse scanner runs at 10AM, 2PM, 6PM and 10PM CAT.
          </div>
          <div style={{ marginTop: 20, padding: '12px 20px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)', display: 'inline-block' }}>
            ⏰ Next scan: 10:00 AM CAT
          </div>
        </div>
      )}

      {pulse && (
        <>
          {/* Key Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10 }}>
            <StatCard label="Public Slips Tracked" value={formatNumber(pulse.totalSlips)} sub="Today's public slips" color="#ec4899" />
            <StatCard label="Total Bettors" value={formatNumber(pulse.totalWeightedCopies)} sub="People backing these slips" color="#8b5cf6" />
            <StatCard label="Avg Legs Per Slip" value={pulse.avgLegs} sub="Typical slip complexity" color="var(--accent)" />
            <StatCard label="Avg Copies Per Slip" value={formatNumber(pulse.avgCopiesPerSlip)} sub="How viral each slip is" color="#f59e0b" />
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg2)', borderRadius: 'var(--radius-sm)', padding: 4, flexWrap: 'wrap' }}>
            {[
              { id: 'overview', label: '📊 Overview' },
              { id: 'teams', label: '⚽ Teams' },
              { id: 'selections', label: '🎯 Selections' },
              { id: 'slips', label: '📋 Top Slips' },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                background: activeTab === t.id ? '#ec4899' : 'transparent',
                color: activeTab === t.id ? '#fff' : 'var(--text3)',
              }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Top Markets */}
              <div className="card" style={{ padding: '16px 20px' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  📊 What Markets is Botswana Backing?
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                  Weighted by number of people backing each slip
                </div>
                {pulse.topMarkets?.slice(0, 10).map((m, i) => (
                  <MarketBar
                    key={m.market}
                    market={m.market}
                    percentage={m.percentage}
                    weightedCopies={m.weightedCopies}
                    max={pulse.topMarkets[0].weightedCopies}
                  />
                ))}
              </div>

              {/* Legs Distribution */}
              <div className="card" style={{ padding: '16px 20px' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  🎲 How Many Legs Are People Using?
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                  Distribution of legs across all public slips
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80 }}>
                  {(() => {
                    const dist = pulse.legsDistribution || [];
                    const max = Math.max(...dist.map(d => d.count), 1);
                    return dist.map((d, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{
                          width: '100%',
                          height: `${(d.count / max) * 60}px`,
                          background: d.legs >= 10 && d.legs <= 15 ? 'var(--accent)' : 'var(--bg3)',
                          borderRadius: '2px 2px 0 0',
                          minHeight: 4,
                        }} />
                        {dist.length < 20 && (
                          <div style={{ fontSize: 9, color: 'var(--text3)' }}>{d.legs}</div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, textAlign: 'center' }}>
                  <span style={{ color: 'var(--accent)' }}>■</span> 10-15 legs (sweet spot based on winner data)
                </div>
              </div>

              {/* Most Copied Slip */}
              {pulse.mostCopiedSlip && (
                <div style={{
                  padding: '16px 20px', background: 'rgba(236,72,153,0.04)',
                  border: '1px solid rgba(236,72,153,0.2)', borderRadius: 'var(--radius)',
                }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4, color: '#ec4899' }}>
                    🔥 Most Copied Slip Today
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>
                        Code: <strong style={{ color: 'var(--accent)' }}>{pulse.mostCopiedSlip.bookingCode}</strong>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                        {pulse.mostCopiedSlip.legs} legs
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: '#ec4899' }}>
                        {formatNumber(pulse.mostCopiedSlip.count)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>people copied this</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Teams Tab */}
          {activeTab === 'teams' && (
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                ⚽ Most Backed Teams in Botswana
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                Teams appearing most in public slips — weighted by copies
              </div>
              {pulse.topTeams?.map((t, i) => (
                <div key={t.team} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: i < 3 ? 'rgba(236,72,153,0.15)' : 'var(--bg3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700,
                      color: i < 3 ? '#ec4899' : 'var(--text3)',
                      flexShrink: 0,
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: i < 3 ? 700 : 400 }}>{t.team}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: i < 3 ? '#ec4899' : 'var(--text2)' }}>
                      {formatNumber(t.weightedCopies)}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>weighted copies</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selections Tab */}
          {activeTab === 'selections' && (
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                🎯 Hottest Selections Right Now
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                Most backed specific picks across all public slips
              </div>
              {pulse.topSelections?.map((s, i) => (
                <div key={i} style={{
                  padding: '12px 0', borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{s.match}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span className="pill pill-teal" style={{ fontSize: 10 }}>{s.market}</span>
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>{s.selection}</span>
                        {s.odds && <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>@ {s.odds}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: '#ec4899' }}>
                        {formatNumber(s.weightedCopies)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>copies</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Top Slips Tab */}
          {activeTab === 'slips' && (
            <div className="card">
              <div className="section-header">
                <div className="section-title">📋 Most Copied Slips Today</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Tap to see selections</div>
              </div>
              {mostCopied.map((slip, i) => (
                <div key={slip.bookingCode}>
                  <div
                    onClick={() => setSelectedSlip(selectedSlip?.bookingCode === slip.bookingCode ? null : slip)}
                    style={{
                      padding: isMobile ? '12px 14px' : '14px 20px',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: selectedSlip?.bookingCode === slip.bookingCode ? 'rgba(236,72,153,0.04)' : 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: i < 3 ? 'rgba(236,72,153,0.15)' : 'var(--bg3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700, color: i < 3 ? '#ec4899' : 'var(--text3)',
                          flexShrink: 0,
                        }}>
                          {i + 1}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{slip.bookingCode}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {slip.legs} legs · {slip.selections?.slice(0,2).map(s => s.homeTeam?.split(' ')[0]).join(', ')}...
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{slip.bookingCode}</div>
<div style={{ fontSize: 11, color: 'var(--text3)' }}>
  {slip.legs} legs · {slip.selections?.slice(0,2).map(s => s.homeTeam?.split(' ')[0]).join(', ')}...
</div>
<div style={{ fontSize: 10, marginTop: 2 }}>
  {slip.validity === 'all_upcoming' && <span style={{ color: 'var(--success)' }}>✅ All legs upcoming</span>}
  {slip.validity === 'partial' && <span style={{ color: 'var(--warning)' }}>⚠️ {slip.startedLegs} leg{slip.startedLegs > 1 ? 's' : ''} started</span>}
  {slip.validity === 'all_started' && <span style={{ color: 'var(--danger)' }}>❌ All legs started</span>}
</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: '#ec4899' }}>
                          {formatNumber(slip.count)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>copies</div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded selections */}
                  {selectedSlip?.bookingCode === slip.bookingCode && (
                    <div style={{ padding: '12px 20px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        All {slip.legs} selections:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {slip.selections?.map((sel, j) => (
                          <div key={j} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '6px 10px', background: 'var(--bg2)', borderRadius: 6, fontSize: 12,
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ color: 'var(--text2)', fontWeight: 500 }}>{sel.homeTeam} vs {sel.awayTeam}</span>
                              <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{sel.market} — {sel.selection}</span>
                            </div>
                            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', flexShrink: 0, marginLeft: 8 }}>
                              {sel.odds}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '0 20px', lineHeight: 1.6 }}>
            ℹ️ Botswana Pulse aggregates anonymous public slip data from Betway BW. This is intelligence data only — not betting advice. Always bet responsibly.
          </div>
        </>
      )}
    </div>
  );
}